import React, { ReactElement, useState, useReducer, useEffect } from 'react';
import { useQuery } from 'react-query';

import { hexToRgba } from '@common/parser';
import { css } from '@emotion/react';
import { AdminExposure } from '@type/api/standardPrice/price';
import { AveragePriceType, ProductsData } from '@type/web/search';

import useRouterHook from '@lib/hook/useRouterHook';
import { palette } from '@lib/styles/palette';
import pixelToRem from '@lib/styles/pixelToRem';

import {
  getSearchPriceMainInfo,
  getSearchPriceRecommendKeyword,
} from '@api/standardPrice.ts/search';

import Icon from '@components/common/Icon';
import * as icons from '@components/common/Icon/svg';
import SkeletonQuickSearch from '@components/common/Skeleton/SearchPrice/SkeletonQuickSearch';
import Carousel from '@components/web/common/swipe/Carousel';

import useDidMountEffect from '../hook/useDidMountEffect';
import PriceGraph from './PriceGraph';

/* ----------------------------- type --------------------------------- */
// interface QuickSearchMenuProp {
//   heading: string;
//   description?: string;
//   titleIcon?: keyof typeof icons;
//   quickSearchMenuItems?: AdminExposure[];
//   selectModelName?: string;
//   selectOptionName?: string;
//   keywordSource?: 'INPUT_SEARCH' | 'QUICK_SEARCH';
//   averagePrices?: ProductsData['averagePrices'];
// }
interface QuickSearchMenuProp {
  heading: string;
  description?: string;
  titleIcon?: keyof typeof icons;
  searchMenuItems: AdminExposure[];
  model?: string | null;
  option?: string | null;
  avgPrice?: AveragePriceType[];
}

// interface quickSearchItemInterface {
//   id: number;
//   keyword: string;
//   adminExposureKeyword: {
//     modelInfoList: {
//       modelName: string;
//       option: string[];
//     }[];
//     modelTotalInfo: {
//       modelNameList: string[];
//       optionList: string[];
//     };
//     keyword: string;
//   };
// }
interface QuickSearchItemInterface {
  id: number;
  keyword: string;
  adminExposureKeyword: {
    modelInfoList: {
      modelName: string;
      option: string[];
    }[];
    modelTotalInfo: {
      modelNameList: string[];
      optionList: string[];
    };
    keyword: string;
  };
}
interface QuickSearchMenuItemProp {
  quickSearchItems: QuickSearchItemInterface[];
  clickHandler: ({
    keyword,
    id,
    adminExposureKeyword,
  }: QuickSearchItemInterface) => void;
}

interface QuickSearchMenuDetailProp {
  setIsSelected: React.Dispatch<React.SetStateAction<boolean>>;
  selectOptionName: string;
  selectModelName: string;
  isQuickSearch: boolean;
  modelInfoListMap: {
    modelNameMap: Map<string, boolean[]>;
    optionMap: Map<string, boolean[]>;
  };
  dispatch: (action: any) => void;
  adminExposureKeyword: {
    modelInfoList: {
      modelName: string;
      option: string[];
    }[];
    modelTotalInfo: {
      modelNameList: string[];
      optionList: string[];
    };
    keyword: string;
  };
  averagePrices?: ProductsData['averagePrices'];
}

type MapType = [string, boolean[]][] | [];

/* ----------------------------- reducer & initialState --------------------------------- */

const initialState = {
  modelNameMap: new Map(),
  optionMap: new Map(),
};

const reducer = (state: typeof initialState, action: any) => {
  switch (action.type) {
    case 'modelName':
      return { ...state, modelNameMap: action.value };
    case 'optionName':
      return { ...state, optionMap: action.value };
    default:
      return {
        modelNameMap: action.value.modelNameMap,
        optionMap: action.value.optionMap,
      };
  }
};

/* ----------------------------- QuickSearchMenu component --------------------------------- */

function QuickSearchMenu({
  heading,
  description,
  option,
  model,
  searchMenuItems,
  avgPrice,
  titleIcon,
}: QuickSearchMenuProp): ReactElement {
  const {
    queryString: { exposureKeyword, keywordSource, priceKeyword },
  } = useRouterHook();

  const isQuickSearch = !!(model && option);
  const [modelInfoListMap, dispatch] = useReducer(reducer, initialState);
  const [isSelected, setIsSelected] = useState(isQuickSearch);
  const [adminExposureKeyword, setAdminExposureKeyword] = useState({
    modelInfoList: [{ modelName: '', option: [''] }],
    modelTotalInfo: {
      modelNameList: [''],
      optionList: [''],
    },
    keyword: '',
  });

  const quickSearchMenuItems = searchMenuItems;
  const averagePrices = avgPrice;
  const selectModelName = model;
  const selectOptionName = option;

  /** 상세 검색을 위한 click 시, 기종과 세부 항목 정보에 대한 상태 업데이트 */
  const clickHandler = ({
    keyword,
    adminExposureKeyword,
  }: QuickSearchItemInterface) => {
    const {
      modelTotalInfo: { modelNameList, optionList },
    } = adminExposureKeyword;

    setIsSelected(true);
    setAdminExposureKeyword({
      ...adminExposureKeyword,
      keyword,
    });

    dispatch({
      value: {
        modelNameMap: new Map(
          /** 버튼의 활성화 여부를 판단하기 위해, 배열에 boolean 값(isDisabled) 추가 */
          modelNameList.map((val) => [val, [false, false]]),
        ),
        optionMap: new Map(
          /** 버튼의 활성화 여부를 판단하기 위해, 배열에 boolean 값(isDisabled) 추가 */
          optionList.map((val) => [val, [true, false]]),
        ),
      },
    });
  };

  /** URL을 이용해 바로 검색하여 들어온 경우, 상태값 세팅 */
  useEffect(() => {
    if (
      !quickSearchItems ||
      !exposureKeyword ||
      !selectModelName ||
      !selectOptionName
    )
      return;

    const [{ adminExposureKeyword }] = quickSearchItems.filter(
      ({ keyword }) => keyword === exposureKeyword,
    );

    const {
      modelInfoList,
      modelTotalInfo: { modelNameList, optionList },
    } = adminExposureKeyword;

    setAdminExposureKeyword({
      ...adminExposureKeyword,
      keyword: exposureKeyword,
    });

    dispatch({
      value: {
        modelNameMap: new Map(
          modelNameList.map((modelName) => {
            const isDisabled = false;
            const isSelected = modelName === selectModelName;

            return [modelName, [isDisabled, isSelected]];
          }),
        ),
        optionMap: new Map(
          optionList.map((optionName) => {
            // 현재 선택된 modelName에 따라 활성화 여부(isDisabled)와 선택 여부(isSelected) 설정
            const [{ option: availableOptions }] = modelInfoList.filter(
              ({ modelName }) => modelName === selectModelName,
            );

            const isDisabled = !(availableOptions.indexOf(optionName) >= 0);
            const isSelected = optionName === selectOptionName;

            return [optionName, [isDisabled, isSelected]];
          }),
        ),
      },
    });
  }, []);

  if (!quickSearchMenuItems) {
    return <SkeletonQuickSearch isSelected={isSelected} />;
  }

  const quickSearchItems = quickSearchMenuItems.map((value, id) => {
    return { ...value, id };
  });

  return (
    <section css={containerStyle(isSelected)} tabIndex={0}>
      <h2 css={headingStyle(isSelected)}>
        <span className="title">{heading}</span>
        {titleIcon && <Icon className="titleIcon" icon={titleIcon} />}
      </h2>
      <h3 css={descriptionStyle(isSelected)} tabIndex={0}>
        {description}
      </h3>
      <div css={contentStyle(isSelected)} tabIndex={0}>
        {isSelected ? (
          // 상세 검색 (기종, 세부 항목 검색)
          <DetailSearch
            isQuickSearch={isQuickSearch}
            setIsSelected={setIsSelected}
            modelInfoListMap={modelInfoListMap}
            dispatch={dispatch}
            adminExposureKeyword={adminExposureKeyword}
            selectModelName={selectModelName ?? ''}
            selectOptionName={selectOptionName ?? ''}
            averagePrices={averagePrices}
          />
        ) : (
          // 퀵 서치 메뉴 (아이폰, 아이패드, 애플워치)
          <SearchMenu
            quickSearchItems={quickSearchItems}
            clickHandler={clickHandler}
          />
        )}
      </div>
    </section>
  );
}

/* ----------------------------- SearchMenus component --------------------------------- */

const SearchMenu = ({
  quickSearchItems,
  clickHandler,
}: QuickSearchMenuItemProp) => {
  return (
    <Carousel>
      {quickSearchItems.map(({ keyword, id, adminExposureKeyword }) => {
        return (
          <button
            type="button"
            aria-label="searchPrice"
            key={id}
            className="priceButtonContainer"
            onClick={() => clickHandler({ keyword, id, adminExposureKeyword })}
          >
            <div className="priceButton">
              <span className="icon">
                <Icon icon="appleIcon" />
              </span>
              <span className="title">#{keyword}</span>
              <span className="subTitle">시세 알아볼까?</span>
            </div>
          </button>
        );
      })}
    </Carousel>
  );
};

/* ----------------------------- DetailSearch component --------------------------------- */

const DetailSearch = ({
  adminExposureKeyword: { modelInfoList, keyword },
  isQuickSearch,
  modelInfoListMap,
  dispatch,
  setIsSelected,
  selectModelName,
  selectOptionName,
  averagePrices,
}: QuickSearchMenuDetailProp) => {
  const { modelNameMap, optionMap } = modelInfoListMap;
  const { push, shallowPush, queryString } = useRouterHook();

  const [modelNameState, setModelNameState] = useState(selectModelName);
  const [optionNameState, setOptionNameState] = useState(selectOptionName);

  const [modelNameList, setModelNameList] = useState<MapType>(
    Array.from(modelNameMap.entries()),
  );
  const [optionNameList, setOptionNameList] = useState<MapType>(
    Array.from(optionMap.entries()),
  );

  // 검색할 기기 옵션 선택
  const selectOptionOf = (option: string) => {
    const isDisabled = optionMap.get(option)?.[0] ?? true;
    const isSelected = true;
    optionMap.set(option, [isDisabled, isSelected]);

    dispatch({ type: 'optionName', value: optionMap });
    setOptionNameState(option);
  };

  // 검색할 기기 모델 선택
  const selectModelOf = (model: string) => {
    const [{ option: availableOptionNameList }] = modelInfoList.filter(
      ({ modelName }) => modelName === model,
    );

    // 초기화
    optionMap.forEach((_, optionName, map) => {
      const isDisabled = true;
      const isSelected = false;

      map.set(optionName, [isDisabled, isSelected]);
    });
    modelNameMap.forEach((_, modelName, map) => {
      const isDisabled = false;
      const isSelected = false;

      map.set(modelName, [isDisabled, isSelected]);
    });

    // 선택 가능한 option만 disable=false 설정
    availableOptionNameList.forEach((optionName) => {
      const isDisabled = false;
      const isSelected = optionNameState
        ? optionName === optionNameState
        : false;

      optionMap.set(optionName, [isDisabled, isSelected]);
    });

    const isDisabled = modelNameMap.get(model)?.[0] ?? true;
    const isSelected = true;
    modelNameMap.set(model, [isDisabled, isSelected]);

    dispatch({ value: { modelNameMap, optionMap } });
    setModelNameState(model);

    // 모델명 선택시, 이미 선택된 옵션(optionNameState)이 선택 가능한지 여부(optionMap의 isSelected)를 체크
    const isSelectedOptionAvailable =
      optionMap.get(optionNameState)?.[1] ?? false;
    if (!isSelectedOptionAvailable) {
      setOptionNameState('');
    }
  };

  useEffect(() => {
    if (modelNameMap.size > 0) {
      setModelNameList(Array.from(modelNameMap.entries()));
    }

    if (optionMap.size > 0) {
      setOptionNameList(Array.from(optionMap.entries()));
    }
  }, [modelInfoListMap]);

  useDidMountEffect(() => {
    if (modelNameState && optionNameState) {
      const searchWord = `${modelNameState} ${optionNameState}`;

      push(
        `/search-price?exposureKeyword=${keyword}&keywordSource=INPUT_SEARCH&searchWord=${searchWord}`,
      );
    }
  }, [modelNameState, optionNameState]);

  return (
    <div css={detailItemStyle}>
      <div className="selectionArea">
        <h2 className="detailItemTitle">
          #{keyword} <span className="divide">시세금액 알아봐!</span>
        </h2>
        <div className="deviceCategory">
          <h3 className="subTitle">기종</h3>
          <div className="categoryContainer">
            {modelNameList.map(([modelName, [isDisabled, isSelected]]) => (
              <button
                type="button"
                aria-label="viewModel"
                className={`button ${isSelected ? 'selected' : ''}`}
                key={modelName}
                disabled={isDisabled}
                onClick={() => selectModelOf(modelName)}
              >
                {modelName}
              </button>
            ))}
          </div>
        </div>
        <div className="detailCategory">
          <h3 className="subTitle">세부항목</h3>
          <div className="categoryContainer">
            {optionNameList.map(([optionName, [isDisabled, isSelected]]) => (
              <button
                type="button"
                aria-label="optionName"
                className={`button ${isSelected ? 'selected' : ''}`}
                key={optionName}
                disabled={isDisabled}
                onClick={() => selectOptionOf(optionName)}
              >
                {optionName}
              </button>
            ))}
          </div>
        </div>
        <div className="goBack">
          <Icon icon="arrowLeft" size={12} />
          <button
            type="button"
            aria-label="browsePrd"
            onClick={() => {
              if (isQuickSearch) {
                shallowPush('/search-price');
              } else {
                setIsSelected(false);
              }
            }}
          >
            다른제품 조회하기
          </button>
        </div>
      </div>
      {isQuickSearch && averagePrices && (
        <div className="grapArea">
          <PriceGraph averagePrices={averagePrices} />
        </div>
      )}
    </div>
  );
};

/* ----------------------------- style --------------------------------- */

const containerStyle = (isSelected: boolean) => css`
  width: 100%;
  height: auto;
  position: relative;

  /** PC */
  @media (min-width: 1024px) {
    margin-top: ${pixelToRem(40)};
    padding: ${pixelToRem(0, 40)};
  }
`;

const headingStyle = (isHide: boolean) => css`
  color: ${palette.black10};
  display: flex;
  justify-content: space-between;
  font-size: ${pixelToRem(20)};
  line-height: ${pixelToRem(26)};
  letter-spacing: ${pixelToRem(-0.5)};

  .titleIcon {
    vertical-align: middle;
    margin-bottom: 3px;
  }

  /** mobile */
  @media (max-width: 599px) {
    display: ${isHide ? 'none' : 'flex'};
    align-items: flex-start;
    padding: ${pixelToRem(0, 20)};
    margin-top: ${pixelToRem(24)};

    .title {
      width: 155px;
    }
  }

  /** tablet */
  @media (min-width: 600px) and (max-width: 1023px) {
    display: ${isHide ? 'none' : 'flex'};
    padding: ${pixelToRem(0, 24)};
    margin-top: ${pixelToRem(24)};
  }

  /** PC */
  @media (min-width: 1024px) {
    justify-content: flex-start;

    .titleIcon {
      margin-left: 8px;
    }
  }
`;

const descriptionStyle = (isHide: boolean) => css`
  color: ${palette.gray5};
  font-size: ${pixelToRem(14)};
  line-height: ${pixelToRem(20)};
  margin-top: ${pixelToRem(4)};
  display: block;

  /** mobile */
  @media (max-width: 599px) {
    display: ${isHide ? 'none' : 'block'};
    padding: ${pixelToRem(0, 20)};
  }

  /** tablet */
  @media (min-width: 600px) and (max-width: 1023px) {
    display: ${isHide ? 'none' : 'block'};
    padding: ${pixelToRem(0, 24)};
  }
`;

const contentStyle = (isSelected: boolean) => css`
  border-top: ${`1px solid ${palette.gray6}`};
  padding-top: ${`${pixelToRem(47)}`};

  .priceButton {
    border-radius: 4px;
    display: inline-flex;
    flex-direction: column;
    padding: ${pixelToRem(20)};
    background-color: ${palette.gray0};
    align-items: flex-start;
  }

  .title {
    margin-top: ${pixelToRem(24)};
    font-size: 18px;
  }

  .subTitle {
    align-self: flex-start;
    font-size: 12px;
    color: ${palette.gray5};
    margin-top: ${pixelToRem(4)};
  }

  /** mobile */
  @media (max-width: 599px) {
    border: 0;
    padding: ${isSelected ? pixelToRem(0) : pixelToRem(26, 0, 0, 20)};
    margin: ${pixelToRem(0, 0, 56, 0)};

    .priceButtonContainer {
      padding-right: 16px;
      background-color: transparent;
    }

    .priceButton {
      min-width: ${pixelToRem(130)};
      height: ${pixelToRem(130)};
    }
  }

  /** tablet */
  @media (min-width: 600px) and (max-width: 1023px) {
    border: 0;
    padding: ${isSelected ? pixelToRem(0) : pixelToRem(20, 24, 0, 24)};
    margin: ${pixelToRem(0, 0, 56, 0)};

    .priceButtonContainer {
      flex: 1 1 auto;
      height: ${pixelToRem(134)};
      background-color: transparent;
    }

    .priceButtonContainer:not(:last-of-type) {
      margin-right: 16px;
    }

    .priceButton {
      width: 100%;
      height: 100%;
    }

    .css-xvikr-carouselContainer {
      justify-content: space-between;
    }
  }

  /** PC */
  @media (min-width: 1024px) {
    margin: ${pixelToRem(30, 0, 92, 0)};

    .priceButtonContainer {
      flex: 1 1 auto;
      height: ${pixelToRem(134)};
      background-color: transparent;
    }

    .priceButtonContainer:not(:last-of-type) {
      margin-right: 16px;
    }

    .priceButton {
      width: 100%;
      height: 100%;
    }
    .css-xvikr-carouselContainer {
      justify-content: space-between;
    }
  }
`;

const detailItemStyle = css`
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    flex-direction: row;
    justify-content: space-between;
  }

  .selectionArea {
    /** mobile */
    @media (max-width: 599px) {
      padding: ${pixelToRem(0, 0, 0, 20)};
      background-color: ${hexToRgba(palette.black10, 0.02)};
    }

    /** tablet */
    @media (min-width: 600px) and (max-width: 1023px) {
      padding: ${pixelToRem(0, 24)};
      background-color: ${hexToRgba(palette.black10, 0.02)};
    }

    /** PC */
    @media (min-width: 1024px) {
      flex: 1 1 50%;
      padding-right: 10px;
    }
  }

  .grapArea {
    /** PC */
    @media (min-width: 1024px) {
      flex: 1 1 50%;
      /* margin-left: 40px; */
    }
  }

  .detailItemTitle {
    font-size: 20px;
    line-height: ${pixelToRem(26)};
    margin-top: ${pixelToRem(24)};

    @media (min-width: 1024px) {
      margin-top: 0;
    }

    .divide {
      @media (max-width: 599px) {
        display: block;
      }

      @media (min-width: 600px) and (max-width: 1023px) {
        display: block;
      }
    }
  }

  .subTitle {
    margin-bottom: ${pixelToRem(12)};
  }

  .deviceCategory {
    margin-top: ${pixelToRem(28)};
    flex: 1 1 auto;

    .css-xvikr-carouselContainer {
      justify-content: flex-start;
    }
  }

  .detailCategory {
    margin-top: ${pixelToRem(24)};
    margin-bottom: ${pixelToRem(38)};

    .css-xvikr-carouselContainer {
      justify-content: flex-start;
    }
  }

  .categoryContainer {
    display: flex;
    flex-wrap: nowrap;
    overflow: auto;
    scroll-snap-type: x mandatory;
    -ms-overflow-style: none;

    ::-webkit-scrollbar {
      display: none;
      width: 0 !important;
    }
  }

  .goBack {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding-right: ${pixelToRem(20)};
    padding-bottom: ${pixelToRem(24)};
    font-size: 12px;

    svg {
      margin-bottom: 2px;
    }

    button {
      background-color: transparent;
      line-height: ${pixelToRem(16)};
    }

    /** PC */
    @media (min-width: 1024px) {
      justify-content: flex-start;
      padding-right: 0;
    }
  }

  .button {
    font-size: ${pixelToRem(14)};
    color: ${palette.gray6};
    border: 1px solid ${palette.gray2};
    border-radius: ${pixelToRem(20)};
    background-color: ${palette.white};
    padding: ${pixelToRem(8, 12)};
    flex: 0 0 auto;
    margin-right: ${pixelToRem(8)};
    scroll-snap-align: start;
  }

  .button.selected {
    color: ${palette.orange9};
    border-color: ${palette.orange9};
  }

  .button:disabled {
    border: 1px solid ${palette.gray2};
    color: ${palette.gray4};
  }
`;

export default QuickSearchMenu;
